import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import API from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import {
  Plus, CheckCircle2, Clock, ArrowRightCircle, Trash2, ExternalLink,
  Copy, Sparkles, Calendar as CalIcon, X, AlertTriangle
} from 'lucide-react';

const STATUS_COLORS = {
  Pending: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  Completed: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Carried Forward': 'bg-violet-500/10 text-violet-300 border-violet-500/20',
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function tomorrowISO() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function DailyPostingTab({ user, clients: clientsProp }) {
  const [date, setDate] = useState(todayISO());
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState(null);
  const [clients, setClients] = useState(clientsProp || []);
  const [loading, setLoading] = useState(true);
  const [fClient, setFClient] = useState('all');
  const [fAllocated, setFAllocated] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [carryPost, setCarryPost] = useState(null);
  const [detailPost, setDetailPost] = useState(null);

  const isAllocator = user?.role === 'admin' || user?.department === "Founder's Office";

  // Keep clients in sync with parent prop, but also fetch locally if empty so
  // the Allocate dialog never opens with an empty client list.
  useEffect(() => {
    if (clientsProp && clientsProp.length > 0) {
      setClients(clientsProp);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
        if (!cancelled) setClients((data || []).filter(c => c.status !== 'Inactive'));
      } catch { /* ignore — dialog will show error */ }
    })();
    return () => { cancelled = true; };
  }, [clientsProp]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = { date };
      if (fClient !== 'all') params.client_id = fClient;
      if (fAllocated !== 'all') params.allocated_to = fAllocated;
      if (fStatus !== 'all') params.status = fStatus;
      const [pr, sr, or_] = await Promise.all([
        axios.get(`${API}/daily-posts`, { withCredentials: true, params }),
        axios.get(`${API}/daily-posts/stats`, { withCredentials: true, params: { date } }),
        axios.get(`${API}/daily-posts/options`, { withCredentials: true }),
      ]);
      setPosts(pr.data); setStats(sr.data); setOptions(or_.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to load posts');
    } finally { setLoading(false); }
  }, [date, fClient, fAllocated, fStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Refetch options on-demand whenever the user opens the Allocate dialog —
  // protects against the case where the initial /options call failed silently
  // and clicking the button would otherwise appear to do nothing.
  const openAllocate = async () => {
    setShowCreate(true);
    if (!options) {
      try {
        const { data } = await axios.get(`${API}/daily-posts/options`, { withCredentials: true });
        setOptions(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load options — please refresh');
      }
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete post "${p.client_name} · ${p.post_type}"?`)) return;
    try {
      await axios.delete(`${API}/daily-posts/${p.post_id}`, { withCredentials: true });
      toast.success('Deleted'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // Group posts by client for the table
  const grouped = useMemo(() => {
    const m = {};
    posts.forEach(p => {
      const k = p.client_id || '—';
      if (!m[k]) m[k] = { client_id: k, client_name: p.client_name, items: [] };
      m[k].items.push(p);
    });
    return Object.values(m).sort((a, b) => a.client_name.localeCompare(b.client_name));
  }, [posts]);

  return (
    <div className="animate-fadeIn" data-testid="daily-posting-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Total" value={stats?.total ?? 0} color="white" testId="kpi-posts-total" />
        <KPI label="Completed" value={stats?.completed ?? 0} color="emerald" testId="kpi-posts-completed" />
        <KPI label="Pending" value={stats?.pending ?? 0} color="amber" testId="kpi-posts-pending" />
        <KPI label="Carried Forward" value={stats?.carried_forward ?? 0} color="violet" testId="kpi-posts-carried" />
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative">
          <CalIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="bg-black/40 border-white/10 text-slate-300 h-9 text-xs pl-8 w-[160px]"
            data-testid="posting-date" />
        </div>
        <Select value={fClient} onValueChange={setFClient}>
          <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="posting-filter-client"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200] max-h-[300px]">
            <SelectItem value="all">All clients</SelectItem>
            {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fAllocated} onValueChange={setFAllocated}>
          <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="posting-filter-allocated"><SelectValue placeholder="All team" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200] max-h-[300px]">
            <SelectItem value="all">All team</SelectItem>
            {(options?.exec_team || []).map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-[140px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="posting-filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="Carried Forward">Carried Forward</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {isAllocator && (
          <Button onClick={openAllocate}
            className="bg-indigo-600 hover:bg-indigo-700 h-9 text-xs" data-testid="allocate-post-btn">
            <Plus size={14} className="mr-1" /> Allocate Post
          </Button>
        )}
      </div>

      {/* Table */}
      {loading && posts.length === 0 ? (
        <div className="glass-card p-12 text-center text-slate-500">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="glass-card p-12 text-center text-slate-500" data-testid="posting-empty">
          <Sparkles size={32} className="mx-auto mb-3 opacity-30" />
          <div className="text-sm">No posts allocated for {fmtDate(date)}.{isAllocator && ' Click "Allocate Post" to start.'}</div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="posting-list">
          {grouped.map(grp => (
            <div key={grp.client_id} className="glass-card overflow-hidden">
              <div className="px-4 py-2.5 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                <div className="text-white font-semibold text-sm">{grp.client_name} <span className="text-slate-500 font-mono text-[10px] ml-1">{grp.client_id}</span></div>
                <span className="text-[10px] text-slate-500">{grp.items.length} post{grp.items.length > 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead><tr>
                    <th>Post Type</th><th>Allocated To</th><th>Platforms</th><th>Status</th><th className="text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {grp.items.map(p => {
                      const total = (p.platforms || []).length;
                      const posted = (p.platforms || []).filter(x => x.posted).length;
                      return (
                        <tr key={p.post_id} className="hover:bg-white/[0.02]" data-testid={`posting-row-${p.post_id}`}>
                          <td>
                            <div className="text-white font-semibold">{p.post_type}</div>
                            <div className="text-[10px] font-mono text-slate-500">{p.post_id}</div>
                          </td>
                          <td className="text-slate-300">{p.allocated_to_name}</td>
                          <td>
                            <div className="text-[10px] text-slate-400">{posted}/{total} posted</div>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {(p.platforms || []).map(pl => (
                                <span key={pl.name}
                                  className={`text-[9px] px-1.5 py-0.5 rounded border ${pl.posted ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-white/5 text-slate-400 border-white/10'}`}>
                                  {pl.name}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td><span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[p.status]}`}>{p.status}</span></td>
                          <td>
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => setDetailPost(p)}
                                className="px-2 py-1 rounded bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 text-[10px]"
                                data-testid={`posting-open-${p.post_id}`}>
                                {p.status === 'Completed' ? 'View' : 'Update'}
                              </button>
                              {isAllocator && p.status !== 'Completed' && (
                                <button onClick={() => setCarryPost(p)}
                                  className="p-1 rounded bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
                                  title="Carry forward"
                                  data-testid={`posting-carry-${p.post_id}`}>
                                  <ArrowRightCircle size={11} />
                                </button>
                              )}
                              {isAllocator && (
                                <>
                                  <button onClick={() => setEditPost(p)}
                                    className="p-1 rounded bg-white/5 text-slate-400 hover:text-white"
                                    title="Edit"
                                    data-testid={`posting-edit-${p.post_id}`}>
                                    <Sparkles size={11} />
                                  </button>
                                  <button onClick={() => remove(p)}
                                    className="p-1 rounded bg-rose-500/10 text-rose-400 hover:bg-rose-500/20"
                                    title="Delete"
                                    data-testid={`posting-delete-${p.post_id}`}>
                                    <Trash2 size={11} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <AllocateDialog options={options} clients={clients} initialDate={date}
          onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll(); }} />
      )}
      {editPost && (
        <AllocateDialog options={options} clients={clients} initialDate={editPost.date}
          existing={editPost}
          onClose={() => setEditPost(null)} onCreated={() => { setEditPost(null); fetchAll(); }} />
      )}
      {carryPost && (
        <CarryForwardDialog post={carryPost} reasons={options?.carry_reasons || []}
          onClose={() => setCarryPost(null)} onDone={() => { setCarryPost(null); fetchAll(); }} />
      )}
      {detailPost && (
        <PostDetailSheet post={detailPost} user={user}
          onClose={() => setDetailPost(null)}
          onUpdated={() => fetchAll()} />
      )}
    </div>
  );
}

function KPI({ label, value, color, testId }) {
  const colorMap = {
    white: 'text-white', emerald: 'text-emerald-400', amber: 'text-amber-400', violet: 'text-violet-400',
  };
  return (
    <div className="glass-card p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl font-heading font-black ${colorMap[color] || 'text-white'}`}>{value}</div>
    </div>
  );
}

// =====================================================================
// ALLOCATE DIALOG
// =====================================================================
function AllocateDialog({ options, clients, initialDate, existing, onClose, onCreated }) {
  const isEdit = !!existing;
  const [date, setDate] = useState(existing?.date || initialDate);
  const [clientId, setClientId] = useState(existing?.client_id || '');
  const [postType, setPostType] = useState(existing?.post_type || '');
  const [allocatedTo, setAllocatedTo] = useState(existing?.allocated_to || '');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const postTypes = options?.post_types || [];
  const execTeam = options?.exec_team || [];
  const optionsLoading = !options;

  // Preview platforms when client + post-type are picked
  useEffect(() => {
    if (!clientId || !postType) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/daily-posts/platforms/${clientId}`,
          { withCredentials: true, params: { post_type: postType } },
        );
        if (!cancelled) setPreview({ ok: true, platforms: data.platforms });
      } catch (e) {
        if (!cancelled) setPreview({ ok: false, msg: e.response?.data?.detail || 'No matching deliverable' });
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, postType]);

  const save = async () => {
    if (!date || !clientId || !postType || !allocatedTo) {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      const body = { date, client_id: clientId, post_type: postType, allocated_to: allocatedTo, notes };
      if (isEdit) {
        await axios.put(`${API}/daily-posts/${existing.post_id}`, body, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/daily-posts`, body, { withCredentials: true });
        toast.success('Post allocated');
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-md" data-testid="allocate-dialog">
        <DialogHeader><DialogTitle className="text-white">{isEdit ? 'Edit Post' : 'Allocate Daily Post'}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <label className="text-slate-400 mb-1 block">Date</label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="alloc-date" />
          </div>
          <div>
            <label className="text-slate-400 mb-1 block">Client</label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="alloc-client"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[300] max-h-[280px]">
                {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-slate-400 mb-1 block">Post Type</label>
            <Select value={postType} onValueChange={setPostType}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="alloc-post-type"><SelectValue placeholder={optionsLoading ? 'Loading…' : 'Pick a type…'} /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">
                {postTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-slate-400 mb-1 block">Allocate To · Social & Content</label>
            <Select value={allocatedTo} onValueChange={setAllocatedTo}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="alloc-assignee"><SelectValue placeholder={optionsLoading ? 'Loading…' : 'Pick a team-member…'} /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[300] max-h-[280px]">
                {execTeam.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {preview && preview.ok && (
            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded p-2 text-[11px]" data-testid="alloc-preview">
              <div className="text-emerald-300 font-semibold mb-1">Platforms (from client scope):</div>
              <div className="flex gap-1 flex-wrap">
                {preview.platforms.length === 0 && <span className="text-slate-500">No platforms configured</span>}
                {preview.platforms.map(p => (
                  <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">{p}</span>
                ))}
              </div>
            </div>
          )}
          {preview && !preview.ok && (
            <div className="bg-rose-500/5 border border-rose-500/15 rounded p-2 text-[11px] text-rose-300 flex items-start gap-1.5" data-testid="alloc-error">
              <AlertTriangle size={12} className="mt-0.5 flex-none" /> {preview.msg}
            </div>
          )}
          <div>
            <label className="text-slate-400 mb-1 block">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-1.5 text-xs"
              data-testid="alloc-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-slate-300 hover:text-white">Cancel</Button>
          <Button onClick={save} disabled={saving || (preview && !preview.ok)}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="alloc-save-btn">
            {saving ? 'Saving…' : (isEdit ? 'Update' : 'Allocate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// CARRY-FORWARD DIALOG
// =====================================================================
function CarryForwardDialog({ post, reasons, onClose, onDone }) {
  const [toDate, setToDate] = useState(tomorrowISO());
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason) { toast.error('Reason is required'); return; }
    if (!toDate) { toast.error('Pick a target date'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/daily-posts/${post.post_id}/carry-forward`,
        { to_date: toDate, reason, details }, { withCredentials: true });
      toast.success(`Carried forward → ${toDate}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Carry forward failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-md" data-testid="carry-dialog">
        <DialogHeader><DialogTitle className="text-white">Carry Forward</DialogTitle></DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="bg-white/[0.02] border border-white/5 rounded p-2 text-[11px]">
            <div className="text-white font-semibold">{post.client_name} · {post.post_type}</div>
            <div className="text-slate-500">{post.post_id} · was scheduled for {fmtDate(post.date)}</div>
          </div>
          <div>
            <label className="text-slate-400 mb-1 block">New Date</label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="carry-to-date" />
          </div>
          <div>
            <label className="text-slate-400 mb-1 block">Reason *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="carry-reason"><SelectValue placeholder="Pick a reason…" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">
                {reasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-slate-400 mb-1 block">Details (optional)</label>
            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={3}
              placeholder="What blocked this post?"
              className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-1.5 text-xs"
              data-testid="carry-details" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-slate-300 hover:text-white">Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="bg-violet-600 hover:bg-violet-700" data-testid="carry-save-btn">
            <ArrowRightCircle size={14} className="mr-1" /> {saving ? 'Saving…' : 'Carry Forward'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// POST DETAIL SHEET — per-platform links + complete
// =====================================================================
function PostDetailSheet({ post: initial, user, onClose, onUpdated }) {
  const [post, setPost] = useState(initial);
  const [savingPlat, setSavingPlat] = useState(null);
  const [completing, setCompleting] = useState(false);

  const canEdit = (user?.role === 'admin' || user?.department === "Founder's Office" ||
                     user?.employee_id === post.allocated_to);
  const isCompleted = post.status === 'Completed';

  const updatePlatform = async (platformName, link, posted) => {
    setSavingPlat(platformName);
    try {
      const { data } = await axios.patch(
        `${API}/daily-posts/${post.post_id}/platform`,
        { platform: platformName, link, posted },
        { withCredentials: true },
      );
      setPost(data);
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSavingPlat(null); }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await axios.post(`${API}/daily-posts/${post.post_id}/complete`,
        {}, { withCredentials: true });
      setPost(data);
      toast.success('Post completed — deliverable progressed');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot complete yet'); }
    finally { setCompleting(false); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success('Link copied'); }
    catch { toast.error('Copy failed'); }
  };

  const total = (post.platforms || []).length;
  const postedCount = (post.platforms || []).filter(p => p.posted && p.link).length;

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="bg-[#0a0a0f] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto" data-testid="post-detail-sheet">
        <SheetHeader>
          <SheetTitle className="text-white">{post.client_name} · {post.post_type}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-[11px] px-2 py-1 rounded border ${STATUS_COLORS[post.status]}`}>{post.status}</span>
            <span className="text-slate-500 text-[10px]">{post.post_id}</span>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded p-2 space-y-0.5 text-[11px]">
            <div><span className="text-slate-500">Date:</span> {post.date}</div>
            <div><span className="text-slate-500">Allocated to:</span> {post.allocated_to_name}</div>
            {post.notes && <div><span className="text-slate-500">Notes:</span> {post.notes}</div>}
          </div>

          <div className="pt-2 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Platforms · {postedCount}/{total} posted
            </div>
            {(post.platforms || []).length === 0 && (
              <div className="text-slate-500 italic">No platforms configured on this client's scope.</div>
            )}
            {(post.platforms || []).map(pl => (
              <PlatformRow key={pl.name} platform={pl} canEdit={canEdit && !isCompleted}
                saving={savingPlat === pl.name}
                onUpdate={(link, posted) => updatePlatform(pl.name, link, posted)}
                onCopy={copyLink} />
            ))}
          </div>

          {(post.carry_history || []).length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Carry-Forward History</div>
              {[...post.carry_history].reverse().map((h, i) => (
                <div key={i} className="bg-violet-500/5 border border-violet-500/10 rounded p-2 mb-1 text-[11px]">
                  <div className="text-white font-semibold">{h.reason}</div>
                  <div className="text-slate-500 text-[10px]">{h.from_date} → {h.to_date} · by {h.rolled_by}</div>
                  {h.details && <div className="text-slate-300 mt-1">{h.details}</div>}
                </div>
              ))}
            </div>
          )}

          {canEdit && !isCompleted && (
            <div className="pt-3 border-t border-white/5">
              <Button onClick={complete} disabled={completing || postedCount < total || total === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                data-testid="post-complete-btn">
                <CheckCircle2 size={14} className="mr-1" />
                {completing ? 'Completing…' : (postedCount < total ? `Post all platforms first (${postedCount}/${total})` : 'Mark Complete')}
              </Button>
              <div className="text-[10px] text-slate-500 mt-1.5 text-center">
                Completion auto-progresses the matched deliverable in the Deliverables tracker.
              </div>
            </div>
          )}

          {post.completed_at && (
            <div className="pt-2 border-t border-white/5 text-[10px] text-slate-500">
              Completed by {post.completed_by} · {new Date(post.completed_at).toLocaleString('en-IN')}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PlatformRow({ platform, canEdit, saving, onUpdate, onCopy }) {
  const [link, setLink] = useState(platform.link || '');

  // Keep local input in sync if parent updates (e.g. after save)
  useEffect(() => { setLink(platform.link || ''); }, [platform.link]);

  const save = () => {
    const trimmed = link.trim();
    onUpdate(trimmed || null, !!trimmed);
  };
  const clear = () => {
    setLink('');
    onUpdate(null, false);
  };

  return (
    <div className="bg-black/30 border border-white/5 rounded p-2 mb-2" data-testid={`platform-row-${platform.name}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-white font-semibold text-[11px]">{platform.name}</span>
          {platform.posted && <CheckCircle2 size={11} className="text-emerald-400" />}
        </div>
        {platform.posted_at && (
          <span className="text-[9px] text-slate-500">
            {new Date(platform.posted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <div className="flex gap-1.5">
        <Input value={link} onChange={e => setLink(e.target.value)}
          disabled={!canEdit || saving}
          placeholder={`https://${platform.name.toLowerCase()}.com/...`}
          className="bg-black/40 border-white/10 text-white h-8 text-[11px]"
          data-testid={`platform-link-${platform.name}`} />
        {canEdit && (
          <Button onClick={save} disabled={saving} size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[10px] px-2"
            data-testid={`platform-save-${platform.name}`}>
            {saving ? '…' : (platform.posted ? 'Update' : 'Save')}
          </Button>
        )}
        {platform.link && (
          <>
            <a href={platform.link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center px-2 rounded bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 h-8" title="Open">
              <ExternalLink size={11} />
            </a>
            <button onClick={() => onCopy(platform.link)}
              className="inline-flex items-center px-2 rounded bg-white/5 text-slate-300 hover:text-white h-8" title="Copy">
              <Copy size={11} />
            </button>
          </>
        )}
        {canEdit && platform.posted && (
          <button onClick={clear}
            className="inline-flex items-center px-2 rounded bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 h-8" title="Reset">
            <X size={11} />
          </button>
        )}
      </div>
    </div>
  );
}
